import { ExtendedBasketAccountAddress } from '@epicuro-next/state/basket-state';
import {
  arrayIsEmpty,
  getDeliveryShopId,
  isNonNullish,
} from '@epicuro-next/utilities';
import { OrderType } from 'graphql-api/gql-api-interfaces';

import {
  BusinessState,
  DataState,
  DeliveryItems,
  DeliveryItemsByPeriod,
  PrefilledDeliverySizeItems,
  PrefilledDeliverySizeItemsByPeriod,
  PrefilledDeliverySizeItemsByShop,
  ProductQuantitiesChange,
  ProductSizeEntities,
  ProductVariant,
} from '../models';
import { getSelectedDeliveryPeriod } from '../shared/get-selected-delivery-period';

import { productQuantitiesBusiness } from './quantities/quantities-change/product-quantity.reducer';

export const DEFAULT_SIZE = 'default';

export const setPrefilledVisibleBusiness = (
  state: BusinessState,
  { visible }: { visible: boolean },
): BusinessState => {
  return {
    ...state,
    prefilledDeliveryItems: {
      ...state.prefilledDeliveryItems,
      visible,
    },
  };
};

export const setMasterPrefilledDeliveryItemsBusiness = (
  state: BusinessState,
  {
    variant,
    sizeField,
    scaleField,
  }: { variant: ProductVariant; scaleField: string; sizeField: string },
): BusinessState => {
  const {
    deliveryItems,
    activeDeliveryShop: { accountId, addressId },
  } = state;

  const selectedDeliveryPeriod = getSelectedDeliveryPeriod(state);

  const sizeProducts = variant.children ?? [];

  // if there is no selectedDeliveryPeriod, then delivery periods matched to NOW delivery
  const deliveryItemsMap = new Map(
    Object.entries(
      deliveryItems[`${accountId}-${addressId}`]?.[
        selectedDeliveryPeriod ?? 'NOW'
      ]?.items,
    ).map(([productId, { quantity }]) => [productId, quantity]),
  );

  return {
    ...state,
    prefilledDeliveryItems: {
      ...state.prefilledDeliveryItems,
      masterQuantities: sizeProducts.reduce(
        (acc: { [key: string]: { [key: string]: number } }, curr) => {
          const key = (curr.attributes?.[scaleField] as string) ?? DEFAULT_SIZE;
          const oldValue: { [key: string]: number } = acc[key] || {};
          return {
            ...acc,
            [key]: {
              ...oldValue,
              [curr.attributes?.[sizeField] as string]:
                deliveryItemsMap.get(curr.productId) ?? 0,
            },
          };
        },
        {},
      ),
    },
  };
};

/**
 * Copies active delivery periods and stores
 * Needs to store interacted deliveryPeriods and stores by user
 */
export const setPrefilledDeliveryItemsBusiness = (
  state: BusinessState,
  {
    category,
    scaleField,
    sizeField,
    sizes,
  }: {
    category: string;
    scaleField: string;
    sizeField: string;
    sizes: ProductSizeEntities;
  },
): BusinessState => {
  const prefilled = state.prefilledDeliveryItems.items;
  const { deliveryItems } = state;

  const newSizes: PrefilledDeliverySizeItemsByShop = Object.entries(
    deliveryItems,
  )
    .map(
      ([deliveryShopKey, deliveryShopPeriods]: [
        string,
        DeliveryItemsByPeriod,
      ]) =>
        Object.entries(deliveryShopPeriods)
          .map(([deliveryPeriodKey, deliveryPeriod]: [string, DeliveryItems]) =>
            Object.entries(deliveryPeriod.items ?? {})
              .map(([productId]): { size: string; width: string } => {
                const size = sizes[productId];

                return {
                  size: size?.attributes?.[sizeField] as string,
                  width:
                    (size?.attributes?.[scaleField] as string) ?? DEFAULT_SIZE,
                };
              })
              .filter(({ size }) => isNonNullish(size))
              .reduce(
                (
                  acc: {
                    [deliveryPeriodKey: string]: { [size: string]: number };
                  },
                  { size, width }: { size: string; width: string },
                ) => {
                  const oldWidthValue = acc[deliveryPeriodKey][width] || {};
                  return {
                    ...acc,
                    [deliveryPeriodKey]: {
                      ...acc[deliveryPeriodKey],
                      [width]: {
                        ...oldWidthValue,
                        [size]:
                          state.prefilledDeliveryItems?.masterQuantities?.[
                            width
                          ]?.[size] ?? 0,
                      },
                    },
                  };
                },
                {
                  [deliveryPeriodKey]: {
                    ...(prefilled?.[deliveryShopKey]?.[deliveryPeriodKey] ??
                      {}),
                  },
                },
              ),
          )
          .reduce(
            (acc, curr) => ({
              ...acc,
              [deliveryShopKey]: {
                ...acc[deliveryShopKey],
                ...curr,
              },
            }),
            {
              [deliveryShopKey]: {
                ...(prefilled?.[deliveryShopKey] ?? {}),
              },
            },
          ),
    )
    .reduce(
      (acc, curr) => ({
        ...acc,
        ...curr,
      }),
      {},
    ) as PrefilledDeliverySizeItemsByShop;

  return {
    ...state,
    prefilledDeliveryItems: {
      ...state.prefilledDeliveryItems,
      category,
      items: newSizes,
    },
  };
};

/**
 * Set master quantities into current variants (DeliveryItems)
 * taking them from prefilledDeliveryItems and getting through the
 * whole process
 */
export const setPrefilledCopyBusiness = (
  state: BusinessState,
  {
    periods,
    shops,
    variants,
    scaleField,
    sizeField,
    data,
  }: {
    periods: string[];
    shops: ExtendedBasketAccountAddress[];
    variants: ProductVariant[];
    scaleField: string;
    sizeField: string;
    data: DataState;
  },
): BusinessState => {
  // if feature is turned off should not copies probably saved previously
  // master quantities to delivery items
  if (!state.prefilledDeliveryItems.visible) {
    return state;
  }

  const shopSet = new Set(shops.map((shop) => getDeliveryShopId(shop)));

  const shopFilteredDeliveryItems: [
    shopKey: string,
    shop: PrefilledDeliverySizeItemsByPeriod,
  ][] = Object.entries(state.prefilledDeliveryItems.items ?? {}).filter(
    ([shopKey]) => shopSet.has(shopKey),
  );

  if (arrayIsEmpty(shopFilteredDeliveryItems)) {
    return state;
  }

  const periodsSet = new Set(periods);
  const periodFilteredDeliveryItems: [
    shopKey: string,
    shop: [
      deliveryPeriod: string,
      deliveryPeriodItems: PrefilledDeliverySizeItems,
    ][],
  ][] = shopFilteredDeliveryItems.map(([shopDeliveryKey, shopDelivery]) => [
    shopDeliveryKey,
    Object.entries(shopDelivery ?? {}).filter(
      ([period]) => periodsSet.has(period) || period === OrderType.Now,
    ),
  ]);

  const changes: ProductQuantitiesChange[] = periodFilteredDeliveryItems
    .map(([shopKey, shop]) =>
      shop.map(([deliveryPeriod, deliveryPeriodItems]) => {
        const deliveryPeriodItemsMap = new Map(
          Object.entries(deliveryPeriodItems).map(([key, value]) => [
            key,
            new Map(Object.entries(value)),
          ]),
        );

        return variants.map((variant) =>
          variant.children
            ?.map(
              (sizeProduct): ProductQuantitiesChange => ({
                productId: sizeProduct.productId,
                accountAddressKey: shopKey,
                quantity:
                  deliveryPeriodItemsMap
                    .get(
                      (sizeProduct?.attributes?.[scaleField] ||
                        DEFAULT_SIZE) as string,
                    )
                    ?.get(sizeProduct.attributes[sizeField] as string) ?? 0,
                deliveryPeriod,
              }),
            )
            .filter(({ quantity }) => quantity > 0),
        );
      }),
    )
    .map((v) => v.flat().flat())
    .reduce((acc, curr) => [...acc, ...(curr ?? [])], [])
    .filter(isNonNullish);

  if (arrayIsEmpty(changes)) {
    return state;
  }

  return {
    ...state,
    ...productQuantitiesBusiness(state, { changes, data }),
    // prefilled delivery items have to make state dirty
    dirty: true,
  };
};
